import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18nContext';
import StaticPageLayout from './StaticPageLayout';

interface Props { onBack: () => void; }

export default function TermsPage({ onBack }: Props) {
  const { language } = useLanguage();
  const en = language === 'en';

  return (
    <StaticPageLayout title={en ? 'Terms of Service' : '服务条款'} onBack={onBack}>
      <Helmet>
        <title>{en ? 'Terms of Service - MinutesAI' : '服务条款 - MinutesAI'}</title>
        <meta name="description" content={en ? 'MinutesAI Terms of Service. Understand your rights, content ownership, and our service commitments.' : 'MinutesAI 服务条款。了解您的权利、内容所有权和我们的服务承诺。'} />
        <link rel="canonical" href="https://minutesai.com/terms" />
        <meta property="og:title" content={en ? 'Terms of Service - MinutesAI' : '服务条款 - MinutesAI'} />
        <meta property="og:url" content="https://minutesai.com/terms" />
      </Helmet>

      <div className="bg-white rounded-2xl border border-surface-border p-8 md:p-12">
        <h2 className="text-3xl font-bold text-brand-900 font-serif mb-2">{en ? 'Terms of Service' : '服务条款'}</h2>
        <p className="text-sm text-ink-muted mb-10">{en ? 'Last updated: March 2026' : '最后更新：2026 年 3 月'}</p>

        <div className="space-y-10 text-ink-secondary leading-relaxed">

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '1. Acceptance of Terms' : '1. 条款接受'}</h3>
            <p>
              {en
                ? 'By accessing or using MinutesAI ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you are using the Service on behalf of an organization, you represent that you have the authority to bind that organization to these Terms. If you do not agree, please do not use our services.'
                : '访问或使用 MinutesAI（"本服务"）即表示您同意受本服务条款（"条款"）的约束。如果您代表组织使用本服务，您声明您有权代表该组织接受本条款。如果您不同意，请勿使用我们的服务。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '2. Service Description' : '2. 服务描述'}</h3>
            <p className="mb-3">
              {en
                ? 'MinutesAI provides an AI-powered meeting transcription, analysis, and note generation platform. Our services include audio file processing, speaker identification, meeting summarization, action item extraction, and document export.'
                : 'MinutesAI 提供 AI 驱动的会议转录、分析和纪要生成平台。我们的服务包括音频文件处理、说话人识别、会议摘要、行动项提取和文档导出。'}
            </p>
            <p>
              {en
                ? 'We reserve the right to modify, suspend, or discontinue any feature of the Service with reasonable notice. For material changes, we will provide at least 30 days advance notice via email or in-product notification.'
                : '我们保留在合理通知后修改、暂停或停止服务任何功能的权利。对于重大变更，我们将通过电子邮件或产品内通知提前至少 30 天告知。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '3. Eligibility & Account Registration' : '3. 资格与账户注册'}</h3>
            <p className="mb-3">
              {en
                ? 'You must be at least 16 years old to use MinutesAI. By creating an account, you confirm that you meet this requirement.'
                : '您必须年满 16 岁才能使用 MinutesAI。创建账户即表示您确认满足此要求。'}
            </p>
            <p>
              {en
                ? 'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account at official@minutes-ai.com.'
                : '您有责任维护账户凭证的保密性，并对账户下发生的所有活动负责。如发现任何未经授权的账户使用，请立即通过 official@minutes-ai.com 通知我们。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '4. Content Ownership & License' : '4. 内容所有权与许可'}</h3>
            <div className="bg-brand-50 border border-brand-200/50 rounded-xl p-5 mb-4">
              <p className="font-bold text-brand-900 mb-1">{en ? 'Your content belongs to you.' : '您的内容属于您。'}</p>
              <p>
                {en
                  ? 'You retain full ownership of all audio files, transcripts, meeting notes, and other content you create or upload through MinutesAI. We do not claim any ownership rights over your content.'
                  : '您对通过 MinutesAI 创建或上传的所有音频文件、转录文本、会议纪要和其他内容保留完整所有权。我们不主张对您的内容拥有任何所有权。'}
              </p>
            </div>
            <p>
              {en
                ? 'By using the Service, you grant MinutesAI a limited, non-exclusive license to process your content solely for the purpose of providing the Service to you. This license terminates when you delete your content or close your account.'
                : '使用本服务即表示您授予 MinutesAI 有限的、非独家的许可，仅用于向您提供服务而处理您的内容。当您删除内容或关闭账户时，此许可终止。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '5. Acceptable Use' : '5. 可接受使用'}</h3>
            <p className="mb-4">{en ? 'You agree not to use the Service to:' : '您同意不使用本服务：'}</p>
            <ul className="space-y-2 pl-4 list-disc list-inside">
              <li>{en ? 'Violate any applicable laws, regulations, or third-party rights' : '违反任何适用法律、法规或第三方权利'}</li>
              <li>{en ? 'Upload content without obtaining all necessary consents from meeting participants' : '在未获得会议参与者所有必要同意的情况下上传内容'}</li>
              <li>{en ? 'Attempt to reverse engineer, decompile, or extract source code from the Service' : '尝试对服务进行逆向工程、反编译或提取源代码'}</li>
              <li>{en ? 'Scrape, crawl, or use automated means to access the Service beyond normal use' : '使用爬虫或自动化手段超出正常使用范围访问服务'}</li>
              <li>{en ? 'Interfere with or disrupt the integrity or performance of the Service' : '干扰或破坏服务的完整性或性能'}</li>
              <li>{en ? 'Use the Service to process content that is illegal, harmful, or violates others\' privacy' : '使用服务处理非法、有害或侵犯他人隐私的内容'}</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '6. Payment & Subscriptions' : '6. 付款与订阅'}</h3>
            <p className="mb-3">
              {en
                ? 'Paid subscriptions are billed in advance on a monthly or annual basis according to the plan selected. All fees are non-refundable except as described below or required by applicable law.'
                : '付费订阅按所选计划按月或按年提前计费。除以下情况或适用法律要求外，所有费用不予退还。'}
            </p>
            <p className="mb-3">
              {en
                ? 'Refund requests submitted within 7 days of initial purchase or renewal will be reviewed on a case-by-case basis. To request a refund, contact official@minutes-ai.com with your order details.'
                : '在首次购买或续订后 7 天内提交的退款请求将逐案审查。如需申请退款，请携带订单详情联系 official@minutes-ai.com。'}
            </p>
            <p>
              {en
                ? 'We reserve the right to suspend access to the Service for accounts with overdue payments. You will receive advance notice before any suspension.'
                : '我们保留暂停逾期付款账户访问服务的权利。在任何暂停之前，您将收到提前通知。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '7. Intellectual Property' : '7. 知识产权'}</h3>
            <p>
              {en
                ? 'The MinutesAI platform, including its software, design, trademarks, and all content created by us, is owned by MinutesAI and protected by applicable intellectual property laws. You may not copy, modify, distribute, or create derivative works based on our platform without our express written permission.'
                : 'MinutesAI 平台，包括其软件、设计、商标和我们创建的所有内容，均归 MinutesAI 所有，并受适用知识产权法律保护。未经我们明确书面许可，您不得复制、修改、分发或基于我们的平台创建衍生作品。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '8. Disclaimer of Warranties' : '8. 免责声明'}</h3>
            <p>
              {en
                ? 'The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the Service will be uninterrupted, error-free, or that transcription output will be 100% accurate. AI-generated content should be reviewed before use in critical decisions.'
                : '本服务按"现状"和"可用状态"提供，不附带任何明示或暗示的保证。我们不保证服务不会中断、无错误，或转录输出 100% 准确。AI 生成的内容在用于关键决策前应经过审查。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '9. Limitation of Liability' : '9. 责任限制'}</h3>
            <p className="mb-3">
              {en
                ? 'To the maximum extent permitted by applicable law, MinutesAI shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities.'
                : '在适用法律允许的最大范围内，MinutesAI 不对任何间接、附带、特殊、后果性或惩罚性损害承担责任，包括利润损失、数据损失或商业机会损失。'}
            </p>
            <p>
              {en
                ? 'Our total aggregate liability to you for any claims arising from or related to the Service shall not exceed the amount you paid to MinutesAI in the 12 months preceding the claim.'
                : '我们对您因服务引起或与服务相关的任何索赔的总累计责任，不超过您在索赔前 12 个月内向 MinutesAI 支付的金额。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '10. Termination' : '10. 终止'}</h3>
            <p className="mb-3">
              {en
                ? 'You may terminate your account at any time by contacting support or through your account settings. Upon termination, your access to the Service will cease and your data will be deleted within 30 days.'
                : '您可以随时通过联系支持团队或通过账户设置终止账户。终止后，您对服务的访问将停止，您的数据将在 30 天内删除。'}
            </p>
            <p>
              {en
                ? 'We may suspend or terminate your account immediately if you materially breach these Terms, engage in fraudulent activity, or if required by law. We will provide notice where reasonably possible.'
                : '如果您严重违反本条款、从事欺诈活动或法律要求，我们可能立即暂停或终止您的账户。在合理可能的情况下，我们将提供通知。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '11. Governing Law' : '11. 适用法律'}</h3>
            <p>
              {en
                ? 'These Terms are governed by the laws of the People\'s Republic of China. Any disputes arising from these Terms or your use of the Service shall be submitted to the competent court with jurisdiction over MinutesAI\'s registered address.'
                : '本条款受中华人民共和国法律管辖。因本条款或您使用本服务引起的任何争议，应提交至对 MinutesAI 注册地址有管辖权的有管辖权法院。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '12. Changes to Terms' : '12. 条款变更'}</h3>
            <p>
              {en
                ? 'We may update these Terms from time to time. We will notify you of material changes via email or a prominent in-product notice at least 14 days before the changes take effect. Continued use of the Service after the effective date constitutes your acceptance of the updated Terms.'
                : '我们可能会不时更新本条款。我们将在变更生效前至少 14 天通过电子邮件或产品内显著通知告知您重大变更。在生效日期后继续使用本服务即表示您接受更新后的条款。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '13. Contact Us' : '13. 联系我们'}</h3>
            <p>{en ? 'For questions about these Terms, please contact:' : '如对本条款有疑问，请联系：'}</p>
            <div className="mt-3 pl-4 space-y-1 text-brand-700">
              <p>official@minutes-ai.com</p>
              <p>{en ? 'Shanghai Minsi Zhihui AI Technology Co., Ltd.' : '上海敏思智汇人工智能科技有限公司'}</p>
            </div>
          </section>

        </div>
      </div>
    </StaticPageLayout>
  );
}
